import type { CategoryKey } from './categories';

export * from './categories';

export type Allocations = Partial<Record<CategoryKey, number>>;

/**
 * The mobile slider clamps the quote to six lines over the photo (TEC-3641); measured, six lines hold
 * 248-262 characters at 360px, so 240 fits whole on every phone. Hard caps on save.
 */
export const testimonialQuoteMaxLength = 240;
export const testimonialAuthorMaxLength = 60;

/** `<p></p>` alone is seven characters of a 240 budget. `CharacterCount` must count with this too. */
export function plainTextLength(value: string): number {
	return value.replace(/<[^>]*>/g, '').replace(/&[^;]+;/g, ' ').length;
}

export const languages = {
	Spanish: 'SPA',
	English: 'ENG',
	Catalan: 'CAT',
	Euskera: 'EUS',
	Gallego: 'GLG',
	German: 'GER',
	French: 'FRE',
	Korean: 'KOR',
	Chinese: 'CHI',
	Japanese: 'JPN',
	Portuguese: 'POR',
	Russian: 'RUS',
	Italian: 'ITA',
	Dutch: 'DUT',
	Greek: 'GRE',
} as const;

export type Language = (typeof languages)[keyof typeof languages];

export const locales = [
	'es',
	'en',
	'ca',
	'eu',
	'gl',
	'de',
	'fr',
	'ko',
	'zh',
	'ja',
	'pt',
	'ru',
	'it',
	'nl',
	'el',
] as const;

export type Locale = (typeof locales)[number];

export const localeToLanguage: Record<Locale, Language> = {
	es: languages.Spanish,
	en: languages.English,
	ca: languages.Catalan,
	eu: languages.Euskera,
	gl: languages.Gallego,
	de: languages.German,
	fr: languages.French,
	ko: languages.Korean,
	zh: languages.Chinese,
	ja: languages.Japanese,
	pt: languages.Portuguese,
	ru: languages.Russian,
	it: languages.Italian,
	nl: languages.Dutch,
	el: languages.Greek,
};

export function localeToLang(locale: string): Language | undefined {
	const normalizedLocale = locale.trim().toLowerCase();
	return localeToLanguage[normalizedLocale as keyof typeof localeToLanguage];
}

export enum merchantOrigin {
	backoffice,
	onboarding,
	onboardingMagento,
	onboardingPrestashop,
	onboardingTpv,
}

export const channelTypes = ['Corporate', 'Tpv', 'Widget', 'API', 'Custom'] as const;

export type ChannelType = (typeof channelTypes)[number];

export const WORLDCOO_MERCHANT_ID = '997d0641-65ab-4897-9719-e4977f733527';

// Routes Patagonia portal donations to the `thanks-donation-portal-patagonia` email template (VSNI-42).
export const PATAGONIA_MERCHANT_ID = 'fd9bfc6d-5853-491a-8413-06373db4be20';

/**
 * Exclusive, in euros, monthly for a subscription: the copy reads "less than" and checks use `>=`.
 * Never spell it out in a message; it is formatted per locale.
 */
export const DONATION_AMOUNT_LIMIT = 3000;

export type DocumentType = 'id' | 'taxId' | 'passport' | 'nie' | 'others';

export const applicationTypes = [
	'merchantDefault',
	'api',
	'widget',
	'backOffice',
	'portal',
	'tpv',
	'recurringDonation',
	'file',
	'paymentWidget',
	'merchantImplementation',
	'extra',
	'shopify',
] as const;

export type ApplicationType = (typeof applicationTypes)[number];

export const commercialTracks = ['pre-plan-b', 'plan-b'] as const;
export type CommercialTrack = (typeof commercialTracks)[number];

export function resolveCommercialTrack(commercialTrack: CommercialTrack | undefined): CommercialTrack {
	return commercialTrack ?? 'pre-plan-b';
}

export function getWorldcooFeeForTrack(commercialTrack: CommercialTrack): number {
	return commercialTrack === 'plan-b' ? 15 : 12;
}

export const commercialTrackLabels: Record<CommercialTrack, string> = {
	'plan-b': 'Plan B',
	'pre-plan-b': 'Pre-Plan B',
};

export const campaignStatuses = ['sketch', 'scheduled', 'active', 'finished', 'disabled'] as const;
export type CampaignStatus = (typeof campaignStatuses)[number];

export const campaignStatusLabels: Record<CampaignStatus, string> = {
	sketch: 'Sketch',
	scheduled: 'Scheduled',
	active: 'Active',
	finished: 'Finished',
	disabled: 'Disabled',
};

export const languageToLocale = {
	[languages.Spanish]: 'es',
	[languages.English]: 'en',
	[languages.Catalan]: 'ca',
	[languages.Euskera]: 'eu',
	[languages.Gallego]: 'gl',
	[languages.German]: 'de',
	[languages.French]: 'fr',
	[languages.Korean]: 'ko',
	[languages.Chinese]: 'zh',
	[languages.Japanese]: 'ja',
	[languages.Portuguese]: 'pt',
	[languages.Russian]: 'ru',
	[languages.Italian]: 'it',
	[languages.Dutch]: 'nl',
	[languages.Greek]: 'el',
} as const satisfies Record<Language, Locale>;
